// A branch's own settings (GET/PATCH /branches/{id}/settings, org.BranchSettings).
import { useEffect, useState } from 'react'
import { api } from './api'
import { optionOn, type OptionKey } from './orgOptions'
import type { BreakReason, Identity } from './types'

export interface BranchSettingsView {
  branchId: string
  handoff: boolean
  handoffLabel: string
  outOfTurnCall: boolean
  outOfTurnReasonRequired: boolean
  callStartsServing: boolean
  breakApproval: boolean
  priorityTickets: boolean
  onlineBooking: boolean
  /** Organisation reasons this branch's staff may not choose. */
  breakReasonsOff: string[]
  /** Each option as the screens obey it: on, and licensed at the branch. */
  effective: Record<string, boolean>
  /** The organisation's reasons, then the branch's own (branchId set). */
  breakReasons: (BreakReason & { on: boolean })[]
}

export type BranchSettingsPatch = Partial<
  Pick<
    BranchSettingsView,
    | 'handoff'
    | 'handoffLabel'
    | 'outOfTurnCall'
    | 'outOfTurnReasonRequired'
    | 'callStartsServing'
    | 'breakApproval'
    | 'priorityTickets'
    | 'onlineBooking'
    | 'breakReasonsOff'
  >
>

/** What "Apply to branches" may copy (org.SettingKey). */
export type SettingKey =
  | 'handoff'
  | 'outOfTurnCall'
  | 'outOfTurnReasonRequired'
  | 'callStartsServing'
  | 'breakApproval'
  | 'breakReasons'
  | 'approvals'
  | 'priorityTickets'
  | 'onlineBooking'

export type SettingSection = 'counter' | 'breaks' | 'approvals' | 'reception' | 'booking'

/** The branch page's sections, in order, with the settings each holds. */
export const SETTING_SECTIONS: { section: SettingSection; keys: SettingKey[] }[] = [
  { section: 'counter', keys: ['handoff', 'outOfTurnCall', 'outOfTurnReasonRequired', 'callStartsServing'] },
  { section: 'breaks', keys: ['breakApproval', 'breakReasons'] },
  { section: 'approvals', keys: ['approvals'] },
  { section: 'reception', keys: ['priorityTickets'] },
  { section: 'booking', keys: ['onlineBooking'] },
]

/**
 * Whether a branch's screens offer an option: the branch's own once read, the
 * person's (on at any branch they reach) until then.
 */
export function branchOptionOn(
  settings: BranchSettingsView | null | undefined,
  identity: Pick<Identity, 'options'> | null | undefined,
  key: OptionKey,
): boolean {
  if (settings) return settings.effective[key] === true
  return optionOn(identity, key)
}

/** The reasons this branch's staff may choose, organisation's first. */
export function offeredReasons(settings: BranchSettingsView | null | undefined): BreakReason[] | null {
  if (!settings) return null
  return settings.breakReasons.filter((r) => r.on)
}

/** One branch's settings, read again when branchId changes; null until read or unreadable. */
export function useBranchSettings(branchId: string | undefined): BranchSettingsView | null {
  const [settings, setSettings] = useState<BranchSettingsView | null>(null)
  useEffect(() => {
    setSettings(null)
    if (!branchId) return
    let alive = true
    api
      .branchSettings(branchId)
      .then((r) => alive && setSettings(r.settings))
      .catch(() => alive && setSettings(null))
    return () => {
      alive = false
    }
  }, [branchId])
  return settings
}
